/**
 * MONSTER TOWN — ZOO CREW ENTRY POINT (src/venues/zoo/**).
 *
 * The one `installZoo(ctx)` line this crew owns in `src/main.ts`. It runs
 * AFTER `installVenue(ctx)`, and that order is load-bearing — see the note on
 * `installZoo` below and the longer one in `src/main.ts`. The short version:
 * `allVenues()` is read POSITIONALLY in two places, so venue #3 must APPEND.
 *
 * WHAT THIS FILE WIRES:
 *   zoo:keeper          the zookeeper was talked to -> the errand arrives, and
 *                       the `talk` step is ticked. This is step 1 of the
 *                       reference errand and the only place it can come from.
 *   zoo:pat:<id>        an animal was patted -> its page in the book opens.
 *   build:placed        an animal was put beside the pen it lives in -> it is
 *                       delighted. Sorting, checked against the real world.
 *   seeding             a brand new town gets the gate and the keeper, so the
 *                       zoo is reachable by a tap on day one.
 *
 * The `deliver` half of the errand lives in `feeding.ts`, at the trough, where
 * the dish actually changes hands.
 */

import { bus } from '../../core/events'
import { errands } from '../../core/errands'
import { clock } from '../../core/time'
import { collection } from '../../core/collection'
import { wallet } from '../../core/economy'
import { save } from '../../core/save'
import { world } from '../../core/scene'
import type { Cell } from '../../core/grid'
import type { InstanceId, PlaceableId, SceneId } from '../../core/types'
import type { GameContext } from '../../app/context'
import { place } from '../../build/placer'
import { registerVenues } from '../../venue/types'
import { registerRoom, roomCenter } from '../../venue/scene'
import { registerAngle, registerSpot } from '../../harness'
import {
  ANIMALS,
  ZOO_SCENE,
  ZOO_TOWN_SEED,
  ZOO_VENUE_DEFS,
  animalByDef,
  zooGate,
  zooKeeper,
} from '../../content/animals'
import { app } from '../../app/context'
import { installZooSheet, animalsHere, openZooSheet, zooSheetState } from './feeding'
import { ANIMAL_HALF_TILES, penRail } from './art'

const TOWN = 'town' as SceneId
const HUNGRY_ERRAND = 'errand_zoo_hungry'
const PAT_PREFIX = 'zoo:pat:'

/** Instances already congratulated for standing in the right place. */
const sorted = new Set<string>()

function hasDef(sceneId: SceneId, defId: PlaceableId): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  return node.grid.all().some((p) => p.defId === defId)
}

function seedOne(sceneId: SceneId, defId: PlaceableId, anchor: Cell): void {
  if (hasDef(sceneId, defId)) return
  place(sceneId, defId, anchor, 0, { silent: true })
}

/** The gate and the keeper, on the south-west front of the street. */
function seedTown(): void {
  seedOne(TOWN, zooGate.id, ZOO_TOWN_SEED.gate)
  seedOne(TOWN, zooKeeper.id, ZOO_TOWN_SEED.keeper)
}

/**
 * THE ERRAND ARRIVES BY BEING ASKED FOR.
 *
 * Tapping the keeper is the `talk` step AND the offer. `isOfferable()` reads the
 * clock (the zookeeper asks between 08:00 and 17:00, and the errand repeats
 * daily), so the errand comes round as part of the world's rhythm rather than at
 * random — and missing a day costs exactly nothing, because it is simply
 * offered again tomorrow (LAW #2).
 *
 * Tapping him when there is nothing to ask is still a whole reaction: he waves,
 * he makes his noise, his bowl wobbles. Never a dead tap (LAW #1).
 */
function talkToKeeper(): void {
  if (errands.isOfferable(HUNGRY_ERRAND)) {
    errands.grant(HUNGRY_ERRAND)
    bus.emit('ui:toast', { icon: 'dinner', ms: 2400 })
  }
  // Ticks step 1 for a running errand; a no-op for anything else. `talk` is one
  // of the three kinds the errand engine cannot adapt for itself.
  bus.emit('errand:observe', { kind: 'talk', what: 'npc_zookeeper', count: 1 })
  bus.emit('fx:play', { effect: 'notes', at: { ...app.playerPos }, count: 6 })
  save.request('zoo:talked')
}

/** Patting an animal is how it gets its page in the book. */
function patAnimal(animalId: string): void {
  const first = !collection.has(animalId)
  collection.found(animalId, { at: { ...app.playerPos } })
  if (first) {
    bus.emit('fx:play', { effect: 'stars', at: { ...app.playerPos }, count: 10 })
    bus.emit('audio:cue', { cue: 'chime_happy' })
    save.request('zoo:patted')
  }
}

/**
 * SORTING, CHECKED AGAINST THE REAL WORLD.
 *
 * An animal put down touching the pen it lives in is delighted. An animal put
 * down anywhere else is just an animal standing there — no cross face, no
 * rejection, no snap-back. The reward exists; the punishment does not.
 */
function checkHabitat(instanceId: InstanceId, defId: string, anchor: Cell): void {
  const a = animalByDef(defId)
  const node = world.get(ZOO_SCENE)
  if (a === undefined || node === undefined) return
  if (sorted.has(String(instanceId))) return

  const wantPen = `zoo_pen_${a.habitat}`
  let matched = false
  for (let dx = -1; dx <= 1 && !matched; dx++) {
    for (let dz = -1; dz <= 1 && !matched; dz++) {
      if (dx === 0 && dz === 0) continue
      const p = node.grid.placementAt({ gx: anchor.gx + dx, gy: 0, gz: anchor.gz + dz })
      if (p !== null && String(p.defId) === wantPen) matched = true
    }
  }
  if (!matched) return

  sorted.add(String(instanceId))
  bus.emit('prop:anim', { instanceId, clip: 'bounce', seconds: 1, target: 'body' })
  bus.emit('fx:play', { effect: 'hearts', at: { ...app.playerPos }, count: 8 })
  bus.emit('audio:cue', { cue: a.sound })
  wallet.earnExact(5, 'zoo.sorted', { ...app.playerPos })
  if (!collection.has(a.id)) collection.found(a.id, { at: { ...app.playerPos } })
  save.request('zoo:sorted')
}

export function installZoo(ctx: GameContext): void {
  /**
   * LAST, and after the venue crew — see the note in `src/main.ts`. Two places
   * read `allVenues()` by INDEX (`[0]` is "the venue", `[1]` is "the
   * restaurant"), so a new venue must append rather than insert.
   *
   * The price of going last is that the economy crew's install-time room loop
   * has already run, so the zoo registers its own room. Everything else still
   * comes from them: the shell is built by `registerRoom` from this venue's own
   * `shell` data, and the starter kit is seeded by their `seedVenues()` on
   * `save:loaded`, which reads `allVenues()` at that point and finds the zoo.
   */
  registerVenues(ZOO_VENUE_DEFS)
  for (const def of ZOO_VENUE_DEFS) {
    registerRoom({ sceneId: def.sceneId, devName: def.devName, grid: def.grid, shell: def.shell })
  }
  installZooSheet(ctx.hud)
  installZooDebug()

  bus.on('spawn:request', (e) => {
    if (e.what === 'zoo:keeper') {
      talkToKeeper()
      return
    }
    if (e.what.startsWith(PAT_PREFIX)) {
      const animalId = e.what.slice(PAT_PREFIX.length)
      if (animalId !== '') patAnimal(animalId)
    }
  })

  bus.on('build:placed', (e) => {
    if (String(e.sceneId) !== String(ZOO_SCENE)) return
    checkHabitat(e.placement.instanceId, String(e.placement.defId), e.placement.anchor)
  })

  bus.on('save:loaded', () => {
    sorted.clear()
    seedTown()
  })

  /**
   * FEEDING TIME COMES ROUND. A new in-world day makes the daily errand
   * offerable again, and the keeper's bowl is how a kid finds out — nothing
   * here nags, and nothing is missed by ignoring it.
   */
  bus.on('time:dayStarted', () => {
    if (errands.isOfferable(HUNGRY_ERRAND)) bus.emit('ui:toast', { icon: 'dinner', ms: 2000 })
  })

  // Harness views, once the economy crew's pass has actually built the room.
  bus.on('app:booted', () => {
    if (!world.has(ZOO_SCENE)) return
    const c = roomCenter(ZOO_SCENE)
    registerAngle({ name: 'zoo', sceneId: ZOO_SCENE, target: c, rot: 0, zoom: 1 })
    registerAngle({ name: 'zoo-pens', sceneId: ZOO_SCENE, target: { ...c, z: c.z - 3 }, rot: 0, zoom: 0 })
    registerSpot('zoo-inside', ZOO_SCENE, c)
    const gateStand = world.get(TOWN)?.grid.cellToWorld({ gx: ZOO_TOWN_SEED.gate.gx + 1, gy: 0, gz: ZOO_TOWN_SEED.gate.gz - 1 })
    if (gateStand !== undefined) registerSpot('zoo-gate', TOWN, gateStand)
  })
}

/** Debug/QA surface for this crew's own tool. Additive, read-only. */
export interface ZooDebugApi {
  animals(): string[]
  errandOfferable(): boolean
  day(): number
  species(): Array<{ id: string; food: string; habitat: string; sound: string }>
}

export interface ZooSheetDebugApi {
  state(): ReturnType<typeof zooSheetState>
  open(): void
}

declare global {
  interface Window {
    __zoo?: ZooDebugApi
    __zooSheet?: ZooSheetDebugApi
    /** vr2: is every animal really inside its own rail? See `penFitReport`. */
    __zooFit?: typeof penFitReport
  }
}

/**
 * ============ IS EVERY ANIMAL REALLY INSIDE ITS OWN RAIL? ============
 *
 * Added for the vr2 "all four animals are drawn outside their enclosure"
 * defect, because that is a MEASUREMENT and until this existed nobody could
 * take it — overlapping on an isometric screenshot is not overlapping in space,
 * and not overlapping on one is not proof either.
 *
 * For every animal in the zoo it finds the pen whose habitat that animal wants,
 * and reports the smallest gap in TILES between the animal's own art silhouette
 * (`ANIMAL_HALF_TILES`) and the four rails `penArt` actually drew (`penRail`).
 * Positive is inside with room; zero or less is the defect. Read-only.
 */
export function penFitReport(): Array<{
  animal: string
  habitat: string
  inside: boolean
  margin: number
}> {
  const node = world.get(ZOO_SCENE)
  const out: Array<{ animal: string; habitat: string; inside: boolean; margin: number }> = []
  if (node === undefined) return out
  const g = node.grid
  const t = g.tileSize
  const pens = node.grid.all().filter((p) => String(p.defId).startsWith('zoo_pen_'))
  for (const p of node.grid.all()) {
    const a = animalByDef(String(p.defId))
    if (a === undefined) continue
    const want = `zoo_pen_${a.habitat}`
    const c = g.footprintCenter(p.anchor, p.footprint, p.rot)
    let best: { margin: number } | null = null
    for (const pen of pens) {
      if (String(pen.defId) !== want) continue
      const pc = g.footprintCenter(pen.anchor, pen.footprint, pen.rot)
      // Rails are drawn in the pen's LOCAL frame; the starter layout never
      // rotates a pen, and a rotated one is reported as "not this pen" rather
      // than measured wrong.
      if (pen.rot !== 0) continue
      const r = penRail(pen.footprint.w, pen.footprint.d)
      const dx = (c.x - pc.x) / t
      const dz = (c.z - pc.z) / t
      const margin = Math.min(
        r.halfW - Math.abs(dx) - ANIMAL_HALF_TILES,
        dz - r.back - ANIMAL_HALF_TILES,
        r.front - dz - ANIMAL_HALF_TILES,
      )
      if (best === null || margin > best.margin) best = { margin }
    }
    if (best === null) continue
    out.push({ animal: a.id, habitat: a.habitat, inside: best.margin > 0, margin: best.margin })
  }
  return out
}

export function installZooDebug(): void {
  window.__zooFit = penFitReport
  window.__zoo = {
    animals: () => animalsHere().map((a) => a.id),
    errandOfferable: () => errands.isOfferable(HUNGRY_ERRAND),
    day: () => clock.day,
    species: () =>
      ANIMALS.map((a) => ({ id: a.id, food: a.food, habitat: a.habitat, sound: a.sound })),
  }
  window.__zooSheet = {
    state: () => zooSheetState(),
    open: () => openZooSheet(),
  }
}
